import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import Toggle from '../toggle/toggle';
import SegmentRulesModal from './SegmentRulesModal';
import { GL_FACILITY, segmentsForConfig, supportsSegmentRules } from '../../utils/gl-segment-rules';

// The toggle track is 22px high (toggle.scss); the gear takes the same height so the
// Facility row is no taller than the others and the default-value inputs stay aligned.
const GEAR_ROW_HEIGHT = '22px';

const GLAccountSegmentConfiguration = ({ 
    updatedClient, 
    data, 
    updateField 
}) => {
    const genericSegments = [
        { key: 'GLUnit', labelKey: 'invoice.unit' },
        { key: 'GLOffice', labelKey: 'office' },
        { key: 'GLDepartement', labelKey: 'invoice.gl_departement' },
        { key: 'GLSection', labelKey: 'invoice.gl_section' },
        { key: 'GLPracticeGroup', labelKey: 'invoice.gl_practice_group' },
        { key: 'GLNatural', labelKey: 'gl_natural' },
        { key: 'GLTimekeeper', labelKey: 'timekeeper' }
    ];
    // XPEN-3746: the firm's accounting system decides which segments show and their names.
    const defaultSegments = segmentsForConfig(updatedClient.config, genericSegments);
    const rulesAllowed = supportsSegmentRules(updatedClient.config);

    const [orderedSegments, setOrderedSegments] = useState(defaultSegments);
    // XPEN-3746: the segment whose rules are open, or null.
    const [rulesSegment, setRulesSegment] = useState(null);

    // Initialize order from config or use default
    useEffect(() => {
        const savedOrder = updatedClient.config?.gl_account_segment_order;
        if (savedOrder && Array.isArray(savedOrder)) {
            // Reorder segments based on saved order
            const ordered = savedOrder
                .map(key => defaultSegments.find(s => s.key === key))
                .filter(Boolean);
            // Add any missing segments at the end
            const missing = defaultSegments.filter(s => !savedOrder.includes(s.key));
            setOrderedSegments([...ordered, ...missing]);
        } else {
            setOrderedSegments(defaultSegments);
        }
    }, [updatedClient.config?.gl_account_segment_order, updatedClient.config?.invoice_exporter?.export_format]);

    const updateSegmentField = (segmentKey, field, value) => {
        const currentConfig = updatedClient.config?.gl_account_segment_configuration || {};
        const currentSegment = currentConfig[segmentKey] || {};
        const updatedSegment = { ...currentSegment, [field]: value };
        const updatedConfig = { ...currentConfig, [segmentKey]: updatedSegment };
        updateField("config", updatedConfig, "gl_account_segment_configuration");
    };

    const handleDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(orderedSegments);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setOrderedSegments(items);

        // Save the new order to config
        const newOrder = items.map(item => item.key);
        updateField("config", newOrder, "gl_account_segment_order");
    };

    return (
        <div className='row mt-3'>
            <div className='col-2'>{data.t.read("client_table.gl_account_segment_configuration")} :</div>
            <div className='col-10'>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="segments" direction="horizontal">
                        {(provided) => (
                            <div 
                                className='d-flex gap-4'
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                {orderedSegments.map((segment, index) => {
                                    const segmentConfig = updatedClient.config?.gl_account_segment_configuration?.[segment.key] || {};
                                    const isActive = segmentConfig.active ?? false;
                                    
                                    return (
                                        <Draggable 
                                            key={segment.key} 
                                            draggableId={segment.key} 
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        opacity: snapshot.isDragging ? 0.8 : 1,
                                                        cursor: 'grab'
                                                    }}
                                                    className='mr-2'
                                                >
                                                    {/* Same row height for every segment, gear or not, so the inputs below line up. */}
                                                    <div className='d-flex align-items-center' style={{ gap: '6px', height: GEAR_ROW_HEIGHT }}
                                                        data-testid={`gl-segment-${segment.key.toLowerCase()}-row`}>
                                                    <Toggle
                                                        isOn={isActive}
                                                        handleToggle={(e) => {
                                                            const active = e.target.checked;
                                                            updateSegmentField(segment.key, "active", active);
                                                        }}
                                                        label={data.t.read(segment.labelKey)}
                                                        id={`gl-segment-${segment.key.toLowerCase()}-toggle`}
                                                    />
                                                    {isActive && rulesAllowed && segment.key === GL_FACILITY && (
                                                        <button
                                                            type='button'
                                                            className={`btn btn-sm p-0 d-inline-flex align-items-center justify-content-center position-relative ${(segmentConfig.rules || []).length ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
                                                            style={{ width: '26px', height: GEAR_ROW_HEIGHT }}
                                                            aria-label={`${data.t.read('segment_rules.title')} ${data.t.read(segment.labelKey)}`}
                                                            title={`${data.t.read('segment_rules.title')} ${data.t.read(segment.labelKey)}`}
                                                            data-testid={`gl-segment-${segment.key.toLowerCase()}-rules`}
                                                            onClick={() => setRulesSegment(segment)}
                                                        >
                                                            <FontAwesomeIcon icon={faCog} />
                                                            {(segmentConfig.rules || []).length > 0 && (
                                                                <span className='badge badge-pill badge-warning position-absolute' style={{ top: '-8px', right: '-8px' }}>
                                                                    {segmentConfig.rules.length + 1}
                                                                </span>
                                                            )}
                                                        </button>
                                                    )}
                                                    </div>
                                                    <input 
                                                        className='form-control mt-1'
                                                        value={segmentConfig.default_value || ""}
                                                        onChange={(e) => updateSegmentField(segment.key, "default_value", e.target.value)}
                                                        placeholder={`${data.t.read(segment.labelKey)}`}
                                                        disabled={!isActive}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            <SegmentRulesModal
                isOpen={rulesSegment !== null}
                segmentKey={rulesSegment?.key}
                segmentLabel={rulesSegment ? data.t.read(rulesSegment.labelKey) : ''}
                rules={rulesSegment ? updatedClient.config?.gl_account_segment_configuration?.[rulesSegment.key]?.rules : []}
                defaultValue={rulesSegment ? updatedClient.config?.gl_account_segment_configuration?.[rulesSegment.key]?.default_value : ''}
                data={data}
                onSave={(rules) => updateSegmentField(rulesSegment.key, 'rules', rules)}
                onClose={() => setRulesSegment(null)}
            />
        </div>
    );
};

export default GLAccountSegmentConfiguration;
